import {
  getGetApiGroupQueryOptions,
  getGetApiInstitutionQueryOptions,
  getGetApiLabQueryOptions,
  type LabViewDTO,
} from '@smartsash/api-client';
import type { TableFilterFieldOverrides } from '@smartsash/ui';
import { useQuery } from '@tanstack/react-query';
import type { TableMeta } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  LABORATORIES_GROUP_FIELD,
  LABORATORIES_INSTITUTE_FIELD,
  laboratoriesColumnText,
  laboratoriesQueryParams,
  type LaboratoriesSearch,
} from './laboratoriesModel';

import { LOOKUP_PAGE_SIZE, useGroupNames, useInstitutionNames } from '@/hooks/useLookupNames';
import { DataScreenFilter, filterColumnsFromText } from '@/ui/shared/TableFilter';

/**
 * The filter builder for the Laboratories list (HS-869 + the filtering-foundation follow-up).
 *
 * Institute and Group are the fields whose `Equals` condition reaches the server, so their
 * vocabularies are ids with names as labels — the same override the page carried. The rows come
 * from the screen's own query key — `laboratoriesQueryParams` builds it identically here and in
 * `laboratoriesScreen`, including the `?groupId=` deep link — which means this is a cache read
 * rather than a second request.
 */
export function LaboratoriesFilter({ search }: { search: LaboratoriesSearch }) {
  const { t } = useTranslation();
  const { data } = useQuery(getGetApiLabQueryOptions(laboratoriesQueryParams(search)));
  const { data: institutions } = useQuery(
    getGetApiInstitutionQueryOptions({ page: 1, pageSize: LOOKUP_PAGE_SIZE }),
  );
  const { data: groups } = useQuery(
    getGetApiGroupQueryOptions({ page: 1, pageSize: LOOKUP_PAGE_SIZE }),
  );
  const institutionNames = useInstitutionNames();
  const groupNames = useGroupNames();
  const meta = useMemo<TableMeta<LabViewDTO>>(
    () => ({ institutionNames, groupNames }),
    [groupNames, institutionNames],
  );

  const columns = useMemo(
    () =>
      filterColumnsFromText<LabViewDTO>(
        {
          company: laboratoriesColumnText.company,
          institute: (row) => laboratoriesColumnText.institute(row, meta),
          group: (row) => laboratoriesColumnText.group(row, meta),
          devices: laboratoriesColumnText.devices,
        },
        (id) => t(`laboratories.columns.${id as 'group'}`),
      ),
    [meta, t],
  );

  const overrides = useMemo<TableFilterFieldOverrides>(
    () => ({
      labels: { [LABORATORIES_INSTITUTE_FIELD]: t('laboratories.filters.institute') },
      options: {
        // The columns render institution and group names; the query takes their ids.
        [LABORATORIES_INSTITUTE_FIELD]: (institutions?.items ?? [])
          .filter((institution) => institution.id != null && institution.name != null)
          .map((institution) => ({ value: institution.id ?? '', label: institution.name ?? '' })),
        [LABORATORIES_GROUP_FIELD]: (groups?.items ?? [])
          .filter((group) => group.id != null && group.name != null)
          .map((group) => ({ value: group.id ?? '', label: group.name ?? '' })),
      },
    }),
    [groups, institutions, t],
  );

  return <DataScreenFilter columns={columns} rows={data?.items ?? []} overrides={overrides} />;
}
